// @ts-strict-ignore
import interpolateComponents from '@automattic/interpolate-components';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { AnchorButton } from 'components/Button/AnchorButton';
import EmailSignupFormContainer from 'containers/EmailSignupFormContainer';
import LoginFormContainer from 'containers/LoginFormContainer';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsApiSignUpBlockData } from 'modules/cms/api/types/CmsApiSignUp';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { gutterWidth, maxWidth } from 'theme';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { getFirstParam } from 'utils/url';
import { useTemporaryStorage } from 'utils/useTemporaryStorage';

import { CmsSignUpBlockContainer } from './CmsSignUpBlockContainer';
import { CmsSignUpBlockHeadline } from './CmsSignUpBlockHeadline';

type Props = {
  data: CmsApiSignUpBlockData;
  isPageHeader: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-prop-types
  content: CmsApiPage;
};

export function CmsSignUpBlock({
  data: {
    headline,
    redirectUrl,
    bgImage,
    loggedInHeadline,
    loggedInRedirectButtonText,
  },
  isPageHeader,
}: Props) {
  const { user } = useUserEnvironment();
  const location = useLocation();
  const tmpId = getFirstParam(location, 'tmpId');
  const [saveTemporaryStorage] = useTemporaryStorage<{
    redirectUrl: string;
  }>(user?.emailVerified ? tmpId : '');
  const [redirectTo, setRedirectTo] = useState(null);
  const [saved, setSaved] = useState(false);
  const [emailInUse, setEmailInUse] = useState('');
  useEffect(() => {
    trackEvent('View Sign Up Form');
  }, []);
  useEffect(() => {
    if (!redirectTo && !tmpId && !saved) {
      setSaved(true);
      saveTemporaryStorage({
        SIGNUP_BLOCK_REDIRECT: {
          redirectUrl,
        },
      }).then((id) => {
        setRedirectTo(
          `${window.location.pathname}${window.location.search}${
            window.location.search ? '&' : '?'
          }tmpId=${id}`
        );
      });
    }
  }, [redirectTo, redirectUrl, saveTemporaryStorage, tmpId, saved]);

  const useLoggedInUi = user && loggedInHeadline && !emailInUse;

  return (
    <CmsSignUpBlockContainer data-qa-id="sign-up">
      <CmsSignUpBlockContainer
        imageHandle={bgImage ? bgImage.handle : null}
        mimetype={bgImage ? bgImage.mimetype : null}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <Box display="flex" justifyContent="flex-end">
            <Box
              width={[1, null, 0.5]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              <CmsSignUpBlockHeadline
                text={useLoggedInUi ? loggedInHeadline : headline}
                isPageHeader={isPageHeader}
              />

              <Box mt="32px">
                {useLoggedInUi && (
                  <>
                    {interpolateComponents({
                      mixedString: sprintf(
                        getText(
                          'You are logged in as {{strong}}%(email)s{{/strong}}'
                        ),
                        {
                          email: user.email,
                        }
                      ),
                      components: {
                        strong: <strong />,
                      },
                    })}
                    {redirectUrl && loggedInRedirectButtonText && (
                      <Box mt={24}>
                        <AnchorButton href={redirectUrl}>
                          {loggedInRedirectButtonText}
                        </AnchorButton>
                      </Box>
                    )}
                  </>
                )}
                {!useLoggedInUi && (
                  <>
                    {!emailInUse && (
                      <EmailSignupFormContainer
                        isModal={false}
                        redirectTo={redirectTo}
                        savedListing={null}
                        subscribedSubsite={null}
                        onLogin={null}
                        onSignup={null}
                        compactView
                        hideLoginBlock
                        customButtonText={getText('Continue')}
                        setEmailInUse={setEmailInUse}
                      />
                    )}
                    {emailInUse && (
                      <LoginFormContainer
                        isModal={false}
                        redirectTo={redirectTo}
                        email={emailInUse}
                        onLogin={null}
                        header=""
                        compactView
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </CmsSignUpBlockContainer>
    </CmsSignUpBlockContainer>
  );
}
